/**
 * Кадрові накази — єдиний список 5 видів (прийом/переміщення/звільнення/
 * зміна нарахувань/відпустка). 2026-08-23 (Норма #29): серверний реєстр
 * /hire-orders/registry (UNION 5 таблиць) + InfiniteDocGrid, фільтри на
 * сервері, колонки «Вкладення» і «Підпис» (sign-control за видом рядка —
 * hire_orders; інші види контролюються у своїх реєстрах).
 */
import { useMemo, useState } from 'react';
import { useOrgFilter } from '@/store/defaults';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef, RowStyle } from 'ag-grid-community';
import { hrOrdersRegistry, type HrOrderRegistryRow } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { DocDateRange, useDocDateRange } from '@/shared/components/DocListFilters';
import { signControl, SIGN_STATUS_LABELS, type SignStatus } from '@/api/signControl';

type Kind = HrOrderRegistryRow['kind'];
const KIND_LABEL: Record<Kind, string> = {
  hire: 'Прийом на роботу',
  transfer: 'Переміщення',
  dismissal: 'Звільнення',
  accrual: 'Зміна нарахувань',
  vacation: 'Відпустка',
  absence: 'Тривала відсутність',
};
const KIND_COLOR: Record<Kind, string> = {
  hire: '#16a34a', transfer: '#2563eb', dismissal: '#dc2626', accrual: '#9333ea',
  vacation: '#ca8a04', absence: '#64748b',
};
const ROUTE: Record<Kind, (id: string) => string> = {
  hire: (id) => `/hire-orders/${id}`,
  transfer: (id) => `/staff-transfers/${id}`,
  dismissal: (id) => `/dismissal-orders/${id}`,
  accrual: (id) => `/payroll/planned-accrual-orders/${id}`,
  vacation: (id) => `/hr/vacation-orders/${id}`,
  absence: (id) => `/absence-orders/${id}`,
};
const ATT_TYPE: Record<Kind, string> = {
  hire: 'doc.hire_orders', transfer: 'doc.staff_transfers', dismissal: 'doc.dismissal_orders',
  accrual: 'doc.planned_accrual_orders', vacation: 'doc.vacation_orders',
  absence: 'doc.absence_orders',
};

export default function HrOrdersPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState<'' | Kind>('');
  const [postedFilter, setPostedFilter] = useState<'' | 'posted' | 'draft'>('');
  // Дати наказу: порожні → період шапки; заповнені → лише вони (ч.775).
  const dr = useDocDateRange();
  const [showNew, setShowNew] = useState(false);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(
    () => new Map((orgList.data?.items ?? []).map((o) => [o.id, o.name])),
    [orgList.data],
  );

  const { datasource, total } = useInfiniteGridDatasource<HrOrderRegistryRow>(
    async (limit, offset, sort) => {
      const page = await hrOrdersRegistry({
        organization_id: orgId || undefined,
        kind: kindFilter || undefined,
        posted: postedFilter === 'posted' ? true : postedFilter === 'draft' ? false : undefined,
        q: q.trim() || undefined,
        date_from: dr.dateFrom, date_to: dr.dateTo,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        limit, offset,
      });
      // Вкладення: документи 5 типів — батч-прапорці по кожному типу окремо.
      try {
        const { attachments } = await import('@/api/attachments');
        const byKind = new Map<Kind, string[]>();
        page.items.forEach((r) => byKind.set(r.kind, [...(byKind.get(r.kind) ?? []), r.id]));
        const flags: Record<string, boolean> = {};
        await Promise.all(Array.from(byKind.entries()).map(async ([k, ids]) => {
          Object.assign(flags, await attachments.flags(ATT_TYPE[k], ids));
        }));
        (page.items as Array<HrOrderRegistryRow & { _att?: boolean }>)
          .forEach((r) => { r._att = !!flags[r.id]; });
      } catch { /* прапорці не критичні */ }
      // Підпис (sign-control): батч по парі (орг, вид) — лише проведені й не з 1С.
      try {
        const groups = new Map<string, { org: string; type: string; ids: string[] }>();
        page.items.forEach((r) => {
          if (!r.posted || r.external_source) return;
          const key = `${r.organization_id}|${r.kind}`;
          const g = groups.get(key) ?? { org: r.organization_id, type: ATT_TYPE[r.kind], ids: [] };
          g.ids.push(r.id); groups.set(key, g);
        });
        const st: Record<string, SignStatus> = {};
        await Promise.all(Array.from(groups.values()).map(async (g) => {
          const res = await signControl.statuses(g.org, g.type, g.ids);
          if (res.enabled && res.controlled) Object.assign(st, res.statuses);
        }));
        (page.items as Array<HrOrderRegistryRow & { __sign_status?: SignStatus }>)
          .forEach((r) => { if (st[r.id]) r.__sign_status = st[r.id]; });
      } catch { /* статуси не критичні */ }
      return page;
    },
    [orgId, kindFilter, postedFilter, q, dr.dateFrom, dr.dateTo],
    // ч.1218: у реєстрі накази кількох таблиць — тип для підказки «не проведено за задумом» беремо за видом.
    { docType: (r) => ATT_TYPE[(r as HrOrderRegistryRow).kind] },
  );

  const cols = useMemo<ColDef<HrOrderRegistryRow>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: HrOrderRegistryRow }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    {
      field: 'document_number', headerName: '№', width: 130,
      cellRenderer: (p: { data?: HrOrderRegistryRow }) => p.data
        ? <a style={{ cursor: 'pointer' }} onClick={() => navigate(ROUTE[p.data!.kind](p.data!.id))}>{p.data.document_number}</a>
        : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      field: 'kind', headerName: 'Вид наказа', width: 170,
      cellRenderer: (p: { data?: HrOrderRegistryRow }) => p.data
        ? <span style={{ color: KIND_COLOR[p.data.kind], fontWeight: 600 }}>{KIND_LABEL[p.data.kind]}</span> : '',
    },
    {
      field: 'organization_id', headerName: 'Організація', width: 190,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'pib', headerName: 'ПIБ', flex: 1, minWidth: 220,
      cellRenderer: (p: { data?: HrOrderRegistryRow }) => !p.data ? ''
        : p.data.pib.startsWith('масовий')
          ? <span style={{ color: 'var(--text-secondary)' }}>{p.data.pib}</span> : p.data.pib,
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
    {
      colId: 'sign_control', headerName: 'Підпис', width: 90, sortable: false,
      cellRenderer: (p: { data?: { __sign_status?: SignStatus } }) => {
        const st = p.data?.__sign_status;
        if (!st) return '';
        if (st === 'UNSIGNED') {
          return <span style={{ color: '#dc2626', fontSize: '0.78rem' }}
            title="Немає підписаного документа (ЕЦП або скан)">очікує</span>;
        }
        return <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }} title={SIGN_STATUS_LABELS[st]}>✓</span>;
      },
    },
  ], [navigate, orgName]);

  const selStyle = { width: 150, flex: '0 0 auto' } as const;

  // «+ Новий»: прийом = створення працівника (наказ виникає разом з карткою);
  // решта — сторінки-редактори у режимі створення.
  const NEW_OPTIONS: Array<{ kind: Kind; label: string; to: string }> = [
    { kind: 'hire', label: 'Прийом на роботу', to: '/employees/new-unified' },
    { kind: 'transfer', label: 'Кадрове переміщення', to: '/staff-transfers/new' },
    { kind: 'dismissal', label: 'Звільнення', to: '/dismissal-orders/new' },
    { kind: 'vacation', label: 'Наказ відпустки', to: '/hr/vacation-orders/new' },
    // ч.1209: догляд за дитиною / військова служба / призупинення трудового договору.
    { kind: 'absence', label: 'Тривала відсутність', to: '/absence-orders/new' },
    { kind: 'accrual', label: 'Зміна нарахувань', to: '/payroll/planned-accrual-orders/new' },
  ];

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>Кадрові накази<TitleTotal total={total} /></h2>
        {/* «+ Новий» з випадаючим вибором виду наказа. */}
        <div style={{ position: 'relative', marginLeft: 'auto' }}
             onMouseLeave={() => setShowNew(false)}>
          <button className="primary" onClick={() => setShowNew((s) => !s)}>+ Новий ▾</button>
          {showNew && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
              background: 'var(--bg-panel)', border: '1px solid var(--border-strong)', borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 200, overflow: 'hidden',
            }}>
              {NEW_OPTIONS.map((o) => (
                <button key={o.kind} onClick={() => { setShowNew(false); navigate(o.to); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    background: 'none', padding: '0.5rem 0.75rem', cursor: 'pointer',
                    color: KIND_COLOR[o.kind], fontWeight: 600,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as '' | Kind)} style={selStyle}>
          <option value="">Усі види</option>
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <select value={postedFilter} onChange={(e) => setPostedFilter(e.target.value as '' | 'posted' | 'draft')} style={selStyle}>
          <option value="">Усі статуси</option>
          <option value="posted">Проведені</option>
          <option value="draft">Чернетки</option>
        </select>
        <input placeholder="ПIБ…" value={q} onChange={(e) => setQ(e.target.value)}
               style={{ width: 180, flex: '0 0 auto' }} />
        <DocDateRange f={dr} />
      </div>

      <InfiniteDocGrid<HrOrderRegistryRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="hr-orders-registry"
        onRowDoubleClicked={(r) => navigate(ROUTE[r.kind](r.id))}
        getRowStyle={(p): RowStyle | undefined => {
          if (p.data?.deletion_mark) return { opacity: 0.4 };
          const st = (p.data as { __sign_status?: SignStatus } | undefined)?.__sign_status;
          return st === 'UNSIGNED' ? { background: 'rgba(220, 38, 38, 0.07)' } : undefined;
        }}
      />
    </div>
  );
}
